import React from "react";
import { TRoadblocksForm, TRoadblocksListItem, TRoadblockStatus } from "../../../../../domain/codecs/form/RoadblockForm";
import { TListingRoadblocksDashboardForm, TListingRoadblocksDashboardItem } from "../../../../../domain/codecs/form/ListingRoadblockForm";
import { pipe } from "fp-ts/lib/function";
import { array } from "fp-ts";
import { CRMTableSheet, TCRMTableColumn, TCRMTableSection } from "../../Simple/CRMTableSheet/CRMTableSheet";
import { CRMRoadblockStatusLabel } from "../CRMRoadblockStatusLabel/CRMRoadblockStatusLabel";
import { WeightBold } from "../../WeightBold/WeightBold";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Text } from "../../BuildingBlocks/Text";
import { CRMAssignUserAvatar } from "../../Complex/CRMAssignUserAvatar/CRMAssignUserAvatar";
import { CRMRoadblockAwaitAvatar } from "../CRMRoadblockAwaitAvatar/CRMRoadblockAwaitAvatar";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { Background } from "../../BuildingBlocks/Background";
import { AnimationPopout } from "../../BuildingBlocks/AnimationPopout";
import { ShineShape } from "../../BuildingBlocks/ShineShape";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMThinColoredButton } from "../../Simple/CRMThinColoredButton/CRMThinColoredButton";
import { TJobsFormIO } from "../../../../../domain/codecs/formIO/JobsIO";
import { TJob1 } from "../../../../../domain/codecs/Job";
import { TGroupSetting, TJobDefinition, getJobDefinition, getJobsInGroupPartition } from "../../CRMJobs/CRMJobsOverlay/CRMJobsDefinitions";
import { getGoToRoadblockRouteAction, makeJobLinkOutOfRouteAction } from "../../CRMJobs/CRMJobsOverlay/CRMJobsCardLinkGenerators";
import { snakeCaseToCopyText } from "../../../../../shared/src/util";
import { capitaliseFirst, underscoreCaseToNormalCase } from "../../../../../shared/src/utilsByDomain/string";
import { trimWithEllipsis } from "../../../../../shared/src/utilsByDomain/string/trimWithEllipsis";
import { TJobsFilter } from "../../../../../domain/codecs/statePartials/JobsPartial";
import { CRMJobAssignedCell } from "../CRMJobAssignedCell/CRMJobAssignedCell";
import { CRMJobDeferCell } from "../CRMJobDeferCell/CRMJobDeferCell";

type TRoadblockTableSheetProps = {
    roadblocksForm: TRoadblocksForm;
    listingRoadblocksForm?: TListingRoadblocksDashboardForm;
    jobsForm: TJobsFormIO;
    jobsFilter: TJobsFilter;
    jobsGroupingMode: TGroupSetting;
    adhocJobsPlacement?: "above_roadblocks";
    onLoadMore: () => void;
    emptyMessage?: string;
    onReassignJob?: (job: TJob1, assignedTo: string) => void;
    onDeferJob?: (job: TJob1, deferredDate: string) => void;
    onAssignListingRoadblock?: (payload: {roadblock_id: string, assigned_to_user_id: string}) => void;
    onAwaitListingRoadblock?: (payload: {roadblock_id: string, awaitable_entities_id: string, days_override: number | null}) => void;
    onCancelListingRoadblockAwait?: (payload: {roadblock_id: string}) => void;
};

type TSheetRow = {
    key: string;
    href: string;
    name: string;
    address: string;
    clients: string;
    status: TRoadblockStatus;
    job?: TJob1;
    assignedToName?: string;
    listingRoadblock?: TListingRoadblocksDashboardItem;
};

// A user row with neither name filled in renders as a bare separator rather than a person,
// so an empty name is treated as no assignee at all.
const assigneeName = (user: {first_name: string, last_name: string} | null): string | undefined => {
    if (user === null) {
        return undefined;
    }
    const name = [user.first_name, user.last_name].filter((part) => part.trim() !== "").join(" ");
    return name === "" ? undefined : name;
};

// The sheet's rows are anchors, so any click inside a cell navigates to the listing unless
// it is stopped here. CRMJobAssignedCell/CRMJobDeferCell guard themselves; the avatars
// reused from the listing sidebar do not, because nothing wraps them there.
const StopRowNavigation = (props: React.PropsWithChildren<{}>): JSX.Element => (
    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        {props.children}
    </div>
);

const isAdhocJob = (job: TJob1): boolean =>
    job.type === "sh_listing_ad_hoc" || job.type === "sh_listing_enquiry_ad_hoc";

const baseColumns: Array<TCRMTableColumn<TSheetRow>> = [
    {
        label: "Name",
        width: "2.5fr",
        render: (row) => <WeightBold>{capitaliseFirst(underscoreCaseToNormalCase(row.name))}</WeightBold>,
    },
    {
        label: "Property",
        width: "2fr",
        render: (row) => trimWithEllipsis(row.address, 45),
    },
    {
        label: "Clients",
        width: "1.5fr",
        render: (row) => trimWithEllipsis(row.clients, 45),
    },
    {
        label: "Status",
        width: "1fr",
        render: (row) => <CRMRoadblockStatusLabel status={row.status} />,
    },
];

export const CRMRoadblockTableSheet = (props: React.PropsWithChildren<TRoadblockTableSheetProps>): JSX.Element => {

    // Roadblocks
    const RoadblockItems: Array<TRoadblocksListItem> =
        props.jobsFilter.filter_by_type === null
            ? props.roadblocksForm.children.roadblocks
            : []
    ;
    const IsLoadingMoreRoadblocks = props.roadblocksForm.status === "submitting" || props.roadblocksForm.status === "success";
    const IsThereMoreToRoadblocksLoad = props.roadblocksForm.children.counts.available_count > props.roadblocksForm.children.roadblocks.length;
    const HasRoadblocks = RoadblockItems.length > 0;

    // Jobs
    const JobItems = pipe(
        props.jobsForm.output.jobs,
        array.filter((job) => {
            let isFilteredByType = props.jobsFilter.filter_by_type !== null;
            let isFilteredByHandler = props.jobsFilter.filter_by_handler !== null;

            if (
                props.roadblocksForm.edited.actionableStatus === "blocked" ||
                props.roadblocksForm.edited.roadblockType !== null
            ) {
                return false;
            }

            if (isFilteredByType && isFilteredByHandler) {
                return (
                    job.type === props.jobsFilter.filter_by_type
                    && job["case_handler_id"] === props.jobsFilter.filter_by_handler
                );
            }

            if (isFilteredByType) {
                return job.type === props.jobsFilter.filter_by_type;
            }

            if (isFilteredByHandler) {
                return job["case_handler_id"] === props.jobsFilter.filter_by_handler;
            }

            return true;
        })
    );
    const { ContextualJobs, GeneralJobs } = pipe(
        props.jobsGroupingMode,
        getJobsInGroupPartition(JobItems),
        ({ left, right }) => ({
            ContextualJobs: right,
            GeneralJobs: left.length > 0 ? left : JobItems
        })
    );
    const HasContextualJobs = ContextualJobs.length > 0;
    const { AdhocJobs, NonAdhocJobs } = pipe(
        GeneralJobs,
        array.partition(isAdhocJob),
        ({ left, right }) => ({ AdhocJobs: right, NonAdhocJobs: left })
    );
    const IsAdhocAboveRoadblocks = props.adhocJobsPlacement === "above_roadblocks";
    const LeadingAdhocJobs = IsAdhocAboveRoadblocks ? AdhocJobs : [];
    const TrailingJobs = IsAdhocAboveRoadblocks ? NonAdhocJobs : GeneralJobs;
    const ContextualJobsGroupName = pipe(
        props.jobsGroupingMode?.groupBy || "",
        snakeCaseToCopyText
    );

    const formatJobName = (jobType: string) => jobType.replace(/^sl_|^sh_/g, '');

    const makeRoadblockLink = (roadblock: TRoadblocksListItem) => pipe(
        getGoToRoadblockRouteAction(roadblock),
        makeJobLinkOutOfRouteAction
    );

    const makeJobLink = (job: TJob1, definition: TJobDefinition) => makeJobLinkOutOfRouteAction(definition.routeAction);

    const getJobName = (job: TJob1, definition: TJobDefinition) => {
        if (job.type === "sh_listing_ad_hoc" || job.type === "sh_listing_enquiry_ad_hoc") {
            return `Adhoc - ${job.task}`;
        }
        if (job.type === "sl_case_ad_hoc") {
            return `Adhoc - ${job.job}`;
        }
        return definition.title ? definition.title : formatJobName(job.type);
    };

    const jobToRow = (keyPrefix: string) => (job: TJob1, index: number): TSheetRow => {
        const definition = getJobDefinition(job);
        return {
            key: `${keyPrefix}-${index}`,
            href: makeJobLink(job, definition),
            name: getJobName(job, definition),
            address: job["address"] || "",
            clients: job["users"] || "",
            status: "open", // Always open for jobs
            job,
        };
    };

    const roadblockRows: Array<TSheetRow> = RoadblockItems.map((item, index) => ({
        key: `rb-${index}`,
        href: makeRoadblockLink(item),
        name: item.type,
        address: item.case_address,
        clients: item.clients,
        status: item.status,
    }));

    const roadblocksFooter =
        <>
            {IsThereMoreToRoadblocksLoad && !IsLoadingMoreRoadblocks &&
                <AnimationPopout>
                    <Background padding={`${CRMSpacing.LARGE} 0px`}>
                        <SpacingRow justifyContent="center">
                            <CRMThinColoredButton
                                backgroundColor={CRMColors.NEUTRAL_2}
                                fontColor={CRMColors.NEUTRAL_12}
                                label={<WeightSemiBold>See more</WeightSemiBold>}
                                onClick={props.onLoadMore}
                                icon="down-arrow"
                            />
                        </SpacingRow>
                    </Background>
                </AnimationPopout>
            }

            {IsThereMoreToRoadblocksLoad && IsLoadingMoreRoadblocks &&
                <Background padding={`${CRMSpacing.LARGE} 0px`}>
                    <SpacingRow justifyContent="center">
                        <ShineShape
                            borderRadius="3px"
                            backgroundColor={CRMColors.NEUTRAL_8}
                            shineColor={CRMColors.NEUTRAL_12}
                            duration={1500}
                            height={"30px"}
                            width={"110px"}
                        />
                    </SpacingRow>
                </Background>
            }
        </>;

    const ListingRoadblockItems = props.listingRoadblocksForm?.children.roadblocks ?? [];
    const HasListingRoadblocks = ListingRoadblockItems.length > 0;

    const listingRoadblockRows: Array<TSheetRow> = ListingRoadblockItems.map((item, index) => ({
        key: `lrb-${index}`,
        href: `/crm/listing/${item.listing_id}?visible_tab=&visible_sections=`,
        name: item.next_item?.display_name || item.listing_roadblock_type,
        address: item.address,
        clients: item.sellers,
        status: item.status,
        assignedToName: assigneeName(item.assigned_to_user),
        listingRoadblock: item,
    }));

    const sections: Array<TCRMTableSection<TSheetRow>> = [
        ...(HasContextualJobs
            ? [{ title: `Jobs in this ${ContextualJobsGroupName}`, rows: ContextualJobs.map(jobToRow("ctx")) }]
            : []),
        ...(LeadingAdhocJobs.length > 0
            ? [{ title: "Ad hoc tasks", rows: LeadingAdhocJobs.map(jobToRow("adhoc")) }]
            : []),
        ...(HasListingRoadblocks
            ? [{ title: "Listing roadblocks", rows: listingRoadblockRows }]
            : []),
        ...(HasRoadblocks
            ? [{ title: "Roadblocks", rows: roadblockRows, footer: roadblocksFooter }]
            : []),
        ...(TrailingJobs.length > 0
            ? [{ title: "Other Jobs", rows: TrailingJobs.map(jobToRow("gen")) }]
            : []),
    ];

    const onReassignJob = props.onReassignJob;
    const onDeferJob = props.onDeferJob;

    const assignedColumns: Array<TCRMTableColumn<TSheetRow>> =
        onReassignJob === undefined
            ? []
            : [{
                label: "Assigned",
                width: "1.5fr",
                render: (row) => {
                    const job = row.job;
                    if (job === undefined || !isAdhocJob(job)) {
                        const listingRoadblock = row.listingRoadblock;
                        const onAssignListingRoadblock = props.onAssignListingRoadblock;

                        if (listingRoadblock === undefined || onAssignListingRoadblock === undefined) {
                            return row.assignedToName === undefined
                                ? null
                                : <Text size={CRMFontSizes.SMALL}>{row.assignedToName}</Text>;
                        }

                        return (
                            <StopRowNavigation>
                                <CRMAssignUserAvatar
                                    size="small"
                                users={props.listingRoadblocksForm?.children.assignable_users ?? []}
                                value={listingRoadblock.assigned_to_user?.id ?? null}
                                    onChange={(assigned_to_user_id) => onAssignListingRoadblock({
                                        roadblock_id: listingRoadblock.id,
                                        assigned_to_user_id,
                                    })}
                                />
                            </StopRowNavigation>
                        );
                    }
                    return (
                        <CRMJobAssignedCell
                            assignedTo={"assigned_to" in job ? job.assigned_to : null}
                            assignableUsers={props.jobsForm.output.adhoc_assignable_users}
                            knownUsers={props.jobsForm.output.all_staff_users}
                            onReassign={(assignedTo) => onReassignJob(job, assignedTo)}
                        />
                    );
                },
            }];

    const deferColumns: Array<TCRMTableColumn<TSheetRow>> =
        onDeferJob === undefined
            ? []
            : [{
                label: "Defer",
                width: "0.75fr",
                render: (row) => {
                    const job = row.job;
                    if (job === undefined || !isAdhocJob(job)) {
                        const listingRoadblock = row.listingRoadblock;
                        const onAwaitListingRoadblock = props.onAwaitListingRoadblock;
                        const onCancelListingRoadblockAwait = props.onCancelListingRoadblockAwait;

                        if (listingRoadblock === undefined
                            || onAwaitListingRoadblock === undefined
                            || onCancelListingRoadblockAwait === undefined
                        ) {
                            return null;
                        }

                        return (
                            <StopRowNavigation>
                                <CRMRoadblockAwaitAvatar
                                    size="small"
                                entities={props.listingRoadblocksForm?.children.awaitable_entities ?? []}
                                activeAwait={
                                    listingRoadblock.blocked_till === null || listingRoadblock.blocked_waiting_on === null
                                        ? null
                                        : {
                                            waiting_on: listingRoadblock.blocked_waiting_on,
                                            blocked_till: listingRoadblock.blocked_till,
                                            is_internal: listingRoadblock.blocked_waiting_on_is_internal,
                                        }
                                }
                                onAwait={(awaitable_entities_id, days_override) => onAwaitListingRoadblock({
                                    roadblock_id: listingRoadblock.id,
                                    awaitable_entities_id,
                                    days_override,
                                })}
                                    onCancelAwait={() => onCancelListingRoadblockAwait({
                                        roadblock_id: listingRoadblock.id,
                                    })}
                                />
                            </StopRowNavigation>
                        );
                    }
                    return (
                        <CRMJobDeferCell
                            onDefer={(deferredDate) => onDeferJob(job, deferredDate)}
                        />
                    );
                },
            }];

    const columns: Array<TCRMTableColumn<TSheetRow>> = [
        ...baseColumns,
        ...assignedColumns,
        ...deferColumns,
    ];

    return (
        <div className="crm-roadblock-jobs-sheet">
            <CRMTableSheet
                fillHeight={true}
                columns={columns}
                sections={sections}
                getRowKey={(row) => row.key}
                getRowHref={(row) => row.href}
                emptyMessage={props.emptyMessage ?? "No roadblocks or jobs to pick up."}
            />
        </div>
    );
};
